import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import FormLogo from "@/components/FormLogo";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, RadioGroup, CheckboxGroup, FileField, TopNotice,
  ApplicantSpocSection, RequestedBySection, SelfSkipSection, makeUploader,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection
} from "@/components/FormShell";

const HALL_OPTIONS = [
  "School Auditorium 2/3 — 1st Floor", "Dome", "Urmila Tai Karad Auditorium",
  "Outside Hotel / Banquet Hall Auditorium", "5/1 Conference Hall",
  "6/1 Conference Hall", "Refectory", "Outdoor Lawn 1/1", "Outdoor Lawn 3/1",
];
const BACKDROP_TYPES = ["Digital", "Banner Printed"];
const BACKDROP_STYLES = ["Digital", "Natural", "Acrylic", "Animated"];
const FOOD_OPTIONS = ["Tea / Coffee", "Snacks", "Hi-Tea", "Breakfast", "Lunch", "Dinner", "Other"];
const VEHICLE_TYPES = [
  "Maini", "School Bus", "School Car — 4 Seater", "School Car — 7 Seater",
  "Vendor Vehicle (Outsourced)",
];
const IT_OPTIONS = ["Projector setup", "Wi-Fi setup"];

export default function NewEvent() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);
  const [form, setForm] = useState({
    // I Event details
    eventName: "", eventDate: "", eventDays: "", eventHours: "",
    eventStartTime: "", eventEndTime: "",
    // II SPOC
    staffName: user?.name || "", staffEmail: user?.email || "", staffContact: "",
    coordTeam: "",
    // III Requested by
    mainDept: "", subDept: "", hodApproval: "", is_hod_self: undefined, is_admin1_self: undefined, admin1_self_cost: "", admin1_self_remark: "",
    // IV Venue
    hallNeeded: "No", hallType: "",
    backdropNeeded: "No", backdropType: "", backdropSize: "",
    backdropStyle: "", backdropColor: "", bannerContent: "",
    photosCount: "", photosType: "",
    schoolLogo: "No", ibLogo: "No",
    soundNeeded: "No", soundSpecs: "",
    itNeeded: "No", itRequirements: [], itOther: "",
    tablesNeeded: "No", tableSize: "", tableCount: "", chairCount: "",
    didiNeeded: "No", didiCount: "",
    hkNeeded: "No", hkCount: "",
    foodNeeded: "No", foodOption: "", foodOther: "",
    vehicleNeeded: "No", vehicleType: "", seatsRequired: "",
    photographerNeeded: "No", videographerNeeded: "No", liveStreamNeeded: "No",
    // V Extra
    additionalNotes: "",
    is_emergency: false,
    emergency_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onUpload = makeUploader(api, setAttachments, setUploading, toast, formatApiError);

  const validate = () => {
    if (!(form.is_hod_self === true || form.is_admin1_self === true) && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?");
      return false;
    }
    const required = ["eventName", "eventDate", "staffName", "staffEmail",
                      "staffContact", "mainDept", "subDept", "hodApproval"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill: ${k}`);
        return false;
      }
    }
    if (form.itNeeded === "Yes" && form.itRequirements.length === 0) {
      toast.error("Select at least one IT requirement.");
      return false;
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/requisitions/event", {
        form_type: "event", parent_ref_code: parentRefCode || undefined,
        form_data: { ...stripSelfSkipTransients(form), attachments },
      });
      toast.success(`Submitted — ${data.ref_code}`);
      nav(`/requisitions/${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="max-w-5xl mx-auto relative">
        <FormLogo />
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#b48a45] font-semibold">
          VGWS-EVT-2026 · Event Requisition
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Event Requisition Form
        </h1>
        <p className="text-sm text-[#4a524c] mt-1">
          Vishwashanti Gurukul World School · Rajbaug, Loni Kalbhor
        </p>

        <TopNotice>
          <div className="font-serif font-semibold mb-1 flex items-center gap-2">
            <AlertCircle size={16} /> Important
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>This is an official indication of an upcoming event, submitted for approval.</li>
            <li>Submit this form at least <strong>21 days</strong> before the event.</li>
            <li>An in-person meeting with the process owner's department must be scheduled to discuss the requirement in detail.</li>
          </ul>
        </TopNotice>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setForm((f) => ({ ...f, emergency_reason: v }))}
        />

        <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="event-form">
          <ResubmitFromSection
            isResubmit={isResubmit}
            onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
            parentRefCode={parentRefCode}
            onSelectHistory={(fd, ref) => {
              setForm((f) => ({ ...f, ...fd,
                staffName: f.staffName || fd.staffName || "",
                staffEmail: f.staffEmail || fd.staffEmail || "",
              }));
              setParentRefCode(ref);
            }}
          />

          {/* I */}
          <Section numeral="I" title="Event details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name of the event" required>
                <input className="input-vgws" required value={form.eventName}
                       onChange={(e) => setField("eventName", e.target.value)} data-testid="field-eventName" />
              </Field>
              <Field label="Date of the event" required>
                <input type="date" className="input-vgws" required value={form.eventDate}
                       onChange={(e) => setField("eventDate", e.target.value)} data-testid="field-eventDate" />
              </Field>
              <Field label="Number of days of event" required>
                <input type="number" min="1" className="input-vgws" value={form.eventDays}
                       onChange={(e) => setField("eventDays", e.target.value)} data-testid="field-eventDays" />
              </Field>
              <Field label="Tentative number of hours of event" required>
                <input type="number" min="0" step="0.5" className="input-vgws" value={form.eventHours}
                       onChange={(e) => setField("eventHours", e.target.value)} data-testid="field-eventHours" />
              </Field>
              <Field label="Tentative event start time" required>
                <input type="time" className="input-vgws" value={form.eventStartTime}
                       onChange={(e) => setField("eventStartTime", e.target.value)} data-testid="field-eventStartTime" />
              </Field>
              <Field label="Tentative event end time" required>
                <input type="time" className="input-vgws" value={form.eventEndTime}
                       onChange={(e) => setField("eventEndTime", e.target.value)} data-testid="field-eventEndTime" />
              </Field>
            </div>
          </Section>

          {/* II SPOC — with extra coord team */}
          <ApplicantSpocSection numeral="II" form={form} setField={setField} />
          <Section numeral="III" title="Event coordination">
            <Field label="Event coordination team member details — name & contact details" required>
              <textarea className="input-vgws min-h-[80px]" required value={form.coordTeam}
                        onChange={(e) => setField("coordTeam", e.target.value)}
                        data-testid="field-coordTeam" />
            </Field>
          </Section>

          {/* IV Requested by */}
          <SelfSkipSection numeral="III·a" form={form} setField={setField} userRole={user?.role} />
          <RequestedBySection numeral="IV" form={form} setField={setField}
                              depts={depts} hods={hods} subDepts={subDepts}  hodOptional={isHodDropdownOptional(form)} />

          {/* V Venue & logistics */}
          <Section numeral="V" title="Venue & logistics requirements">
            <Field label="Do you need to book a hall?" required>
              <RadioGroup name="hallNeeded" value={form.hallNeeded}
                          onChange={(v) => setField("hallNeeded", v)}
                          options={["Yes", "No"]} testid="field-hallNeeded" />
            </Field>
            {form.hallNeeded === "Yes" && (
              <Field label="Type of hall" required>
                <select className="input-vgws" value={form.hallType}
                        onChange={(e) => setField("hallType", e.target.value)}
                        data-testid="field-hallType">
                  <option value="">Select hall</option>
                  {HALL_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </Field>
            )}

            <div className="pt-3 border-t border-[#f2ecdf]">
              <div className="font-serif text-sm font-semibold text-[#7a1f2b] mb-3">Event requirements</div>
              <Field label="Backdrop required?" required>
                <RadioGroup name="backdropNeeded" value={form.backdropNeeded}
                            onChange={(v) => setField("backdropNeeded", v)}
                            options={["Yes", "No"]} testid="field-backdropNeeded" />
              </Field>
              {form.backdropNeeded === "Yes" && (
                <div className="grid sm:grid-cols-2 gap-4 mt-3">
                  <Field label="Type of backdrop" required>
                    <select className="input-vgws" value={form.backdropType}
                            onChange={(e) => setField("backdropType", e.target.value)}
                            data-testid="field-backdropType">
                      <option value="">Select type</option>
                      {BACKDROP_TYPES.map((v) => <option key={v}>{v}</option>)}
                    </select>
                  </Field>
                  <Field label="Backdrop size" required>
                    <input className="input-vgws" value={form.backdropSize}
                           onChange={(e) => setField("backdropSize", e.target.value)}
                           data-testid="field-backdropSize" />
                  </Field>
                  <Field label="Backdrop style" required>
                    <select className="input-vgws" value={form.backdropStyle}
                            onChange={(e) => setField("backdropStyle", e.target.value)}
                            data-testid="field-backdropStyle">
                      <option value="">Select style</option>
                      {BACKDROP_STYLES.map((v) => <option key={v}>{v}</option>)}
                    </select>
                  </Field>
                  <Field label="Colour combination" required>
                    <input className="input-vgws" value={form.backdropColor}
                           onChange={(e) => setField("backdropColor", e.target.value)}
                           data-testid="field-backdropColor" />
                  </Field>
                  <Field label="Banner content draft" required>
                    <textarea className="input-vgws min-h-[70px]" value={form.bannerContent}
                              onChange={(e) => setField("bannerContent", e.target.value)}
                              data-testid="field-bannerContent" />
                  </Field>
                  <Field label="Number of photos" required>
                    <input type="number" min="0" className="input-vgws" value={form.photosCount}
                           onChange={(e) => setField("photosCount", e.target.value)}
                           data-testid="field-photosCount" />
                  </Field>
                  <Field label="Type of photos" required>
                    <input className="input-vgws" value={form.photosType}
                           onChange={(e) => setField("photosType", e.target.value)}
                           data-testid="field-photosType" />
                  </Field>
                  <FileField label="Attach backdrop design draft" fieldName="backdropDraft"
                             accept=".pdf,.jpg,.jpeg,.png"
                             attachments={attachments} onChange={(e) => onUpload("backdropDraft", e)}
                             disabled={uploading} testid="attach-backdropDraft" />
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <Field label="School logo required?" required>
                  <RadioGroup name="schoolLogo" value={form.schoolLogo}
                              onChange={(v) => setField("schoolLogo", v)}
                              options={["Yes", "No"]} testid="field-schoolLogo" />
                </Field>
                <Field label="IB logo required?" required>
                  <RadioGroup name="ibLogo" value={form.ibLogo}
                              onChange={(v) => setField("ibLogo", v)}
                              options={["Yes", "No"]} testid="field-ibLogo" />
                </Field>
              </div>
              <div className="mt-3 text-xs text-[#6e7770]"><strong>Note:</strong> Only the approved design must be used further.</div>
            </div>

            <div className="pt-3 border-t border-[#f2ecdf]">
              <Field label="Sound system required?" required>
                <RadioGroup name="soundNeeded" value={form.soundNeeded}
                            onChange={(v) => setField("soundNeeded", v)}
                            options={["Yes", "No"]} testid="field-soundNeeded" />
              </Field>
              {form.soundNeeded === "Yes" && (
                <Field label="Specifications of system in detail" required>
                  <textarea className="input-vgws min-h-[70px]" value={form.soundSpecs}
                            onChange={(e) => setField("soundSpecs", e.target.value)}
                            data-testid="field-soundSpecs" />
                </Field>
              )}
              <Field label="IT support required?" required>
                <RadioGroup name="itNeeded" value={form.itNeeded}
                            onChange={(v) => setField("itNeeded", v)}
                            options={["Yes", "No"]} testid="field-itNeeded" />
              </Field>
              {form.itNeeded === "Yes" && (
                <>
                  <Field label="IT requirements" required hint="Select at least one">
                    <CheckboxGroup value={form.itRequirements}
                                   onChange={(v) => setField("itRequirements", v)}
                                   options={IT_OPTIONS} testid="field-itRequirements" />
                  </Field>
                  <Field label="Any other description for IT requirements">
                    <textarea className="input-vgws min-h-[60px]" value={form.itOther}
                              onChange={(e) => setField("itOther", e.target.value)}
                              data-testid="field-itOther" />
                  </Field>
                </>
              )}
            </div>

            <div className="pt-3 border-t border-[#f2ecdf]">
              <div className="font-serif text-sm font-semibold text-[#7a1f2b] mb-3">Seating & support</div>
              <Field label="Table & chair set-up to display projects?" required>
                <RadioGroup name="tablesNeeded" value={form.tablesNeeded}
                            onChange={(v) => setField("tablesNeeded", v)}
                            options={["Yes", "No"]} testid="field-tablesNeeded" />
              </Field>
              {form.tablesNeeded === "Yes" && (
                <div className="grid sm:grid-cols-3 gap-4 mt-3">
                  <Field label="Table size" required>
                    <input className="input-vgws" value={form.tableSize}
                           onChange={(e) => setField("tableSize", e.target.value)}
                           data-testid="field-tableSize" />
                  </Field>
                  <Field label="Table count" required>
                    <input type="number" min="0" className="input-vgws" value={form.tableCount}
                           onChange={(e) => setField("tableCount", e.target.value)}
                           data-testid="field-tableCount" />
                  </Field>
                  <Field label="Chair count" required>
                    <input type="number" min="0" className="input-vgws" value={form.chairCount}
                           onChange={(e) => setField("chairCount", e.target.value)}
                           data-testid="field-chairCount" />
                  </Field>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <Field label="Didi & attendant support?" required>
                  <RadioGroup name="didiNeeded" value={form.didiNeeded}
                              onChange={(v) => setField("didiNeeded", v)}
                              options={["Yes", "No"]} testid="field-didiNeeded" />
                </Field>
                {form.didiNeeded === "Yes" && (
                  <Field label="Didi & attendants count" required>
                    <input type="number" min="0" className="input-vgws" value={form.didiCount}
                           onChange={(e) => setField("didiCount", e.target.value)}
                           data-testid="field-didiCount" />
                  </Field>
                )}
                <Field label="Housekeeping required?" required>
                  <RadioGroup name="hkNeeded" value={form.hkNeeded}
                              onChange={(v) => setField("hkNeeded", v)}
                              options={["Yes", "No"]} testid="field-hkNeeded" />
                </Field>
                {form.hkNeeded === "Yes" && (
                  <Field label="How many housekeeping staff required" required>
                    <input type="number" min="0" className="input-vgws" value={form.hkCount}
                           onChange={(e) => setField("hkCount", e.target.value)}
                           data-testid="field-hkCount" />
                  </Field>
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-[#f2ecdf]">
              <div className="font-serif text-sm font-semibold text-[#7a1f2b] mb-3">Food & refreshments</div>
              <Field label="Food / snacks / hi-tea requirement?" required>
                <RadioGroup name="foodNeeded" value={form.foodNeeded}
                            onChange={(v) => setField("foodNeeded", v)}
                            options={["Yes", "No"]} testid="field-foodNeeded" />
              </Field>
              {form.foodNeeded === "Yes" && (
                <>
                  <Field label="Select requirement" required>
                    <select className="input-vgws" value={form.foodOption}
                            onChange={(e) => setField("foodOption", e.target.value)}
                            data-testid="field-foodOption">
                      <option value="">Select option</option>
                      {FOOD_OPTIONS.map((v) => <option key={v}>{v}</option>)}
                    </select>
                  </Field>
                  {form.foodOption === "Other" && (
                    <Field label="Please specify" required>
                      <input className="input-vgws" value={form.foodOther}
                             onChange={(e) => setField("foodOther", e.target.value)}
                             data-testid="field-foodOther" />
                    </Field>
                  )}
                  <FileField label="Attach specific food menu for the event" fieldName="foodMenu"
                             accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                             attachments={attachments} onChange={(e) => onUpload("foodMenu", e)}
                             disabled={uploading} testid="attach-foodMenu" optional />
                </>
              )}
            </div>

            <div className="pt-3 border-t border-[#f2ecdf]">
              <div className="font-serif text-sm font-semibold text-[#7a1f2b] mb-3">Vehicle</div>
              <Field label="Requirement of vehicle?" required>
                <RadioGroup name="vehicleNeeded" value={form.vehicleNeeded}
                            onChange={(v) => setField("vehicleNeeded", v)}
                            options={["Yes", "No"]} testid="field-vehicleNeeded" />
              </Field>
              {form.vehicleNeeded === "Yes" && (
                <div className="grid sm:grid-cols-2 gap-4 mt-3">
                  <Field label="Type of vehicle" required>
                    <select className="input-vgws" value={form.vehicleType}
                            onChange={(e) => setField("vehicleType", e.target.value)}
                            data-testid="field-vehicleType">
                      <option value="">Select vehicle</option>
                      {VEHICLE_TYPES.map((v) => <option key={v}>{v}</option>)}
                    </select>
                  </Field>
                  <Field label="Number of seats required" required>
                    <input type="number" min="0" className="input-vgws" value={form.seatsRequired}
                           onChange={(e) => setField("seatsRequired", e.target.value)}
                           data-testid="field-seatsRequired" />
                  </Field>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-[#f2ecdf]">
              <div className="font-serif text-sm font-semibold text-[#7a1f2b] mb-3">Media coverage</div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Photographer required?" required>
                  <RadioGroup name="photographerNeeded" value={form.photographerNeeded}
                              onChange={(v) => setField("photographerNeeded", v)}
                              options={["Yes", "No"]} testid="field-photographerNeeded" />
                </Field>
                <Field label="Videographer required?" required>
                  <RadioGroup name="videographerNeeded" value={form.videographerNeeded}
                              onChange={(v) => setField("videographerNeeded", v)}
                              options={["Yes", "No"]} testid="field-videographerNeeded" />
                </Field>
                <Field label="Live stream of event?" required>
                  <RadioGroup name="liveStreamNeeded" value={form.liveStreamNeeded}
                              onChange={(v) => setField("liveStreamNeeded", v)}
                              options={["Yes", "No"]} testid="field-liveStreamNeeded" />
                </Field>
              </div>
            </div>
          </Section>

          <Section numeral="VI" title="Any other requirements">
            <Field label="Additional notes for the events team">
              <textarea className="input-vgws min-h-[90px]" value={form.additionalNotes}
                        onChange={(e) => setField("additionalNotes", e.target.value)}
                        data-testid="field-additionalNotes" />
            </Field>
          </Section>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[#6e7770] max-w-md">
              Fields marked <span className="text-[#b3261e]">*</span> are required.
            </p>
            <button type="submit" disabled={submitting} className="btn-vgws" data-testid="submit-event-button">
              <Send size={16} /> {submitting ? "Submitting…" : "Submit requisition"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
